import React from 'react';
import { Plus } from 'lucide-react';
import { InfoCircleFilled } from '@ant-design/icons';
import { AutomationNodeType, AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';

// Mapping of node types and subtypes to their empty state text
const EMPTY_STATE_TEXT_MAPPING = {
  // Delay Node
  [AutomationNodeType.DELAY]: 'Add Delay Time',

  // Condition Node
  [AutomationNodeType.CONDITION]: 'Add Condition',

  // Action Node subtypes
  [`${AutomationNodeType.ACTION}_${AutomationActionNodeSubType.ADD_TO_OFFER}`]: 'Select Offer',
  [`${AutomationNodeType.ACTION}_${AutomationActionNodeSubType.SEND_PRODUCT_CATALOG}`]: 'Select Catalog',
  [`${AutomationNodeType.ACTION}_${AutomationActionNodeSubType.SEND_EMAIL}`]: 'Select Email Template',
  [`${AutomationNodeType.ACTION}_${AutomationActionNodeSubType.UPDATE_MEMBER_DATA}`]: 'Configure Action',

  // UPDATE_MEMBER_DATA subtypes based on metadata.type
  [`${AutomationNodeType.ACTION}_${AutomationActionNodeSubType.UPDATE_MEMBER_DATA}_ADD_TAG`]: 'Select Tag',
  [`${AutomationNodeType.ACTION}_${AutomationActionNodeSubType.UPDATE_MEMBER_DATA}_ADD_TO_GROUP`]: 'Select Group',
  [`${AutomationNodeType.ACTION}_${AutomationActionNodeSubType.UPDATE_MEMBER_DATA}_UPDATE_MEMBER_INFORMATION`]: 'Select Fields',

  // Default fallback for action nodes
  [AutomationNodeType.ACTION]: 'Configure Action',
} as const;

interface EmptyStateButtonProps {
  nodeType: AutomationNodeType;
  actionSubType?: AutomationActionNodeSubType;
  onClick: (e?: React.MouseEvent) => void; // Accept optional event parameter
  customText?: string; // Override default text if needed
  metadata?: any; // Node metadata to access type for UPDATE_MEMBER_DATA subtypes
}

export const EmptyStateButton: React.FC<EmptyStateButtonProps> = ({
  nodeType,
  actionSubType,
  onClick,
  customText,
  metadata
}) => {
  // Determine the button text
  const getButtonText = (): string => {
    if (customText) return customText;

    // For action nodes, try to get subtype-specific text first
    if (nodeType === AutomationNodeType.ACTION && actionSubType) {
      // Special handling for UPDATE_MEMBER_DATA subtypes based on metadata.type
      if (actionSubType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && metadata?.type) {
        const memberDataKey = `${nodeType}_${actionSubType}_${metadata.type}` as keyof typeof EMPTY_STATE_TEXT_MAPPING;
        const memberDataText = EMPTY_STATE_TEXT_MAPPING[memberDataKey];
        if (memberDataText) {
          return memberDataText;
        }
      }

      const subtypeKey = `${nodeType}_${actionSubType}` as keyof typeof EMPTY_STATE_TEXT_MAPPING;
      return EMPTY_STATE_TEXT_MAPPING[subtypeKey] || EMPTY_STATE_TEXT_MAPPING[nodeType];
    }

    return EMPTY_STATE_TEXT_MAPPING[nodeType] || 'Configure';
  };

  // Use consistent styling for all nodes
  const getButtonStyles = () => {
    return {
      buttonClass: 'inline-flex flex-col items-center gap-2 px-2 py-0.5 relative flex-[0_0_auto] bg-white rounded-xl border border-dashed border-gray-300 hover:bg-grey-1',
      textClass: 'relative w-fit mt-[-1.00px] font-medium text-grey-5 text-xs tracking-[0] leading-[1.5] whitespace-nowrap',
      iconClass: 'w-3 h-3 text-grey-5'
    };
  };

  // Get tooltip text
  const getTooltipText = (): string => {
    return 'Required';
  };

  const buttonText = getButtonText();
  const styles = getButtonStyles();
  const tooltipText = getTooltipText();

  // Render info icon with or without tooltip
  const renderInfoIcon = () => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-flex h-3 items-center justify-center text-yellow-5" aria-label={tooltipText}>
              <InfoCircleFilled />
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium">
            {tooltipText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-yellow-1 rounded-xl overflow-hidden">
      <Button
        variant="outline"
        className={styles.buttonClass}
        onClick={(e) => onClick(e)}
      >
        <div className="gap-2 inline-flex items-center relative flex-[0_0_auto]">
          <Plus className={styles.iconClass} />
          <div className={styles.textClass}>
            {buttonText}
          </div>
        </div>
      </Button>
      {renderInfoIcon()}
    </div>
  );
};
